import { TFunction } from 'react-i18next';
import * as Yup from 'yup';

import {
  FormDataFranchise,
  FranchiseType,
} from '../../../../../types/api/franchises';
import { AddressInitialValues } from '../../../../../utils/constants/address';
import { isStreetFreeInputAllowed } from '../../../../../utils/constants/misc';
import { invalidTabHeaderIcon } from '../../../../../utils/globals';
import {
  formatMobileNumber,
  formatPhoneNumber,
  getPhoneorMobileNumberRegion,
  isValidMobileNumber,
  isValidPhoneNumber,
} from '../../../../../utils/helpers/phoneNumbers';
import { isEmail } from '../../../../../utils/helpers/validation';
import {
  yupMaxField,
  yupMinField,
  yupRequiredField,
} from '../../../../../utils/helpers/yup';
import { getAddressSchema } from '../../../../validations/address';

type TabName = 'general-info' | 'allowances';

export function getInitialValues(
  data: FranchiseType | undefined,
  isEditDialog: boolean
) {
  return {
    id: isEditDialog ? data?.id ?? 0 : 0,
    name: isEditDialog ? data?.name ?? '' : '',
    mobile: isEditDialog ? data?.mobile ?? '' : '',
    mobile_region: getPhoneorMobileNumberRegion(''),
    phone: isEditDialog ? data?.phone ?? '' : '',
    phone_region: getPhoneorMobileNumberRegion(''),
    email: isEditDialog ? data?.email ?? '' : '',
    address: isEditDialog
      ? data?.address ?? AddressInitialValues
      : AddressInitialValues,
    incomingCashPercent: isEditDialog ? data?.incomingCashPercent ?? 0 : 0,
    incomingInvoicePercent: isEditDialog
      ? data?.incomingInvoicePercent ?? 0
      : 0,
    internationalCashPercent: isEditDialog
      ? data?.internationalCashPercent ?? 0
      : 0,
    internationalInvoicePercent: isEditDialog
      ? data?.internationalInvoicePercent ?? 0
      : 0,
    outgoingCashPercent: isEditDialog ? data?.outgoingCashPercent ?? 0 : 0,
    outgoingInvoicePercent: isEditDialog
      ? data?.outgoingInvoicePercent ?? 0
      : 0,
    ssNumber: isEditDialog ? data?.ssNumber ?? '' : '',
    taxNumber: isEditDialog ? data?.taxNumber ?? '' : '',
  };
}

export function toApiData(values: FormDataFranchise) {
  let apiData = {
    ...values,
  };

  if (!apiData.email) {
    delete apiData.email;
  }

  if (!apiData.taxNumber) {
    delete apiData.taxNumber;
  }

  if (!apiData.ssNumber) {
    delete apiData.ssNumber;
  }

  if (!apiData.id) {
    delete apiData.id;
  }

  if (apiData.phone && apiData.phone_region) {
    apiData.phone = formatPhoneNumber(apiData.phone, apiData.phone_region);
  }

  if (apiData.mobile && apiData.mobile_region) {
    apiData.mobile = formatMobileNumber(apiData.mobile, apiData.mobile_region);
  }

  delete apiData.phone_region;
  delete apiData.mobile_region;

  return apiData;
}

export function getValidationSchema(t: TFunction) {
  return Yup.object().shape({
    name: Yup.string()
      .required(yupRequiredField(t, t('Name')))
      .min(1, ({ min }) => yupMinField(t, t('Name'), min))
      .max(45, ({ max }) => yupMaxField(t, t('Name'), max)),
    email: Yup.string().test(
      'email',
      t('Invalid email address entered!'),
      (value: any) => {
        if (value?.length > 0) {
          return isEmail(value);
        } else {
          return true;
        }
      }
    ),
    mobile: Yup.string()
      .test(
        'valid-mobile',
        t('Invalid mobile phone number'),
        (value, context) => {
          if (!value) return true;
          const { mobile_region } = context.parent;
          return isValidMobileNumber(value, mobile_region);
        }
      )
      .test(
        'mobile-or-phone',
        t('At least mobile or phone number must be filled out'),
        function (value) {
          const { phone } = this.parent;
          return !!(value || phone);
        }
      ),

    phone: Yup.string().test(
      'valid-phone',
      t('Invalid phone number'),
      (value, context) => {
        if (!value) return true;
        const { phone_region } = context.parent;
        return isValidPhoneNumber(value, phone_region);
      }
    ),
    address: getAddressSchema(t, {
      isStreetFreeInputAllowed,
      municipality: true,
      RequiredHouseNumber: true,
    }),
    incomingCashPercent: Yup.number(),
    incomingInvoicePercent: Yup.number(),
    internationalCashPercent: Yup.number(),
    internationalInvoicePercent: Yup.number(),
    outgoingCashPercent: Yup.number(),
    outgoingInvoicePercent: Yup.number(),
    ssNumber: Yup.string().nullable().optional(),
    taxNumber: Yup.string().nullable().optional(),
  });
}

export function tabIcon(tabName: TabName, errors: any) {
  const hasTabErrors = getHasTabErrors();
  return hasTabErrors(tabName, errors) ? invalidTabHeaderIcon : {};
}

function getHasTabErrors() {
  return function hasTabErrors(tabName: TabName, errors: any) {
    const errorsKeys = Object.keys(errors);

    const tabNameFieldsMap = {
      'general-info': {
        name: true,
        address: true,
      },
      allowances: {
        incomingCashPercent: true,
        incomingInvoicePercent: true,
        internationalCashPercent: true,
        internationalInvoicePercent: true,
        outgoingCashPercent: true,
        outgoingInvoicePercent: true,
      },
    };

    const tabFields = Object.keys(tabNameFieldsMap[tabName]);

    for (const field of tabFields) {
      if (errorsKeys.includes(field)) {
        return true;
      }
    }

    return false;
  };
}
